import React, { useCallback, useRef, useState } from 'react'
import { useLocation } from 'react-router-dom'

export type Updatable<T> = T & { update: (props: Partial<T>) => void }

/**
 * To use this Context template, you will have to render <Provider> at a component that parents all places that use the context.
 * Then, you can use useContext react hook to get the states in the context (look up how to use context for class or function components)
 * @param defaultState the starting states
 * @param onComponentDidMount callback when the component is mounted
 * @param onComponentDidUpdate callback when the component is updated
 */
export function generateStateContext<T>(
  defaultState: T,
  onComponentDidMount?: (props: Updatable<T>) => void,
  onComponentDidUpdate?: (props: Updatable<T>) => void
) {
  const Context = React.createContext<Updatable<T>>({ ...defaultState, update: () => {} })

  class Provider extends React.Component<{}, Updatable<T>> {
    constructor(props: {}) {
      super(props)
      this.state = {
        ...defaultState,
        update: this.update
      }
    }

    update = (props: Partial<T>) => {
      const newState: any = {
        ...props
      }
      this.setState(newState)
    }

    componentDidMount() {
      if (onComponentDidMount) onComponentDidMount(this.state)
    }

    componentDidUpdate() {
      if (onComponentDidUpdate) onComponentDidUpdate(this.state)
    }

    render() {
      return <Context.Provider value={this.state}>{this.props.children}</Context.Provider>
    }
  }

  return { Context, Provider }
}

export function useParam(paramName: string) {
  const location = useLocation()
  const params = new URLSearchParams(location.search)
  return params.get(paramName)
}

export function useForceUpdate() {
  const [, setState] = useState(false)
  const stateRef = useRef(false)

  const update = useCallback(() => {
    stateRef.current = !stateRef.current
    setState(stateRef.current)
  }, [stateRef])

  return update
}
